import { ShareBarCell, TextAnchorCell, TextCell } from "@similarweb/pro-logic-components";
import type { ColumnsType, ColumnType } from "@similarweb/pro-logic-components";

import { translate } from "app/filters/i18nFilter";
import { formatShare } from "app/filters/ngFilters";
import type { SwitcherOption } from "app/pages/digital-marketing/ad-intelligence/common/shared/ShareTypeSwitcher";
import { NA_VALUE } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";

import {
    formatIndustryForAPI,
    getIndustryLabel,
    getMetricKey,
    getTopIndustriesIndexColumn,
    VALUE_COLUMN_TITLE_KEYS,
} from "./topIndustriesMetric";
import type { ITopIndustryCompareRow } from "./useTopIndustries";

export const getTopIndustriesCompareColumns = (
    selectedSwitcherOption: SwitcherOption,
    domains: string[],
    currentPage: number,
): ColumnsType<ITopIndustryCompareRow> => {
    const industryColumn = {
        title: () => translate("ad.intelligence.publisher.overview.top.industries.column.title"),
        dataIndex: "category",
        render: (_category: string, row: ITopIndustryCompareRow) => (
            <TextAnchorCell
                href={displayAdvertisingNavigation.overview.getLink({
                    industry: formatIndustryForAPI(row),
                })}
            >
                {getIndustryLabel(row)}
            </TextAnchorCell>
        ),
    } satisfies ColumnType<ITopIndustryCompareRow>;

    const dataKey = getMetricKey(selectedSwitcherOption);

    const valueColumn = {
        title: translate(VALUE_COLUMN_TITLE_KEYS[dataKey]),
        align: "left" as const,
        dataIndex: dataKey,
        // ShareBarCell renders each value as a literal `value * 100%` width — it does not
        // normalize the array itself, so the competitive share between the compared domains
        // has to be computed here from the raw per-domain values, not read off `*Share`
        // (which is each domain's share of its own grand total, the wrong denominator).
        render: (rawByDomain: Record<string, number>) => {
            const total = rawByDomain ? domains.reduce((sum, domain) => sum + (rawByDomain[domain] ?? 0), 0) : 0;

            if (!total) {
                return <TextCell>{NA_VALUE}</TextCell>;
            }

            return (
                <ShareBarCell
                    values={domains.map((domain) => (rawByDomain[domain] ?? 0) / total)}
                    tooltip={{
                        title: translate("ad.intelligence.publisher.overview.top.industries.compare.tooltip", {
                            metric: translate(VALUE_COLUMN_TITLE_KEYS[dataKey]),
                        }),
                        labels: domains,
                    }}
                    valueFormatter={(value) => formatShare(value, 1, 0.01, NA_VALUE) as string}
                />
            );
        },
    } satisfies ColumnType<ITopIndustryCompareRow>;

    return [getTopIndustriesIndexColumn(currentPage), industryColumn, valueColumn];
};
